import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WizardDataContext from '../store/WizardDataContext';
import { couponApiHeaders, isCouponTokenForbidden, COUPON_TOKEN_INVALID_MESSAGE } from '../utils/couponApiUtils';
import { clearPaymentPending } from '../utils/paymentLock';

const PaymentError = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wizardCtx = useContext(WizardDataContext);
  const { wizardData } = wizardCtx;
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Payment didn't go through — drop the pending lock so the user can retry.
  useEffect(() => {
    clearPaymentPending();
  }, []);

  const companyId = wizardData?.meta?.companyId;
  const couponId = wizardData?.meta?.couponId;
  const creatorToken = wizardData?.meta?.creatorToken;

  // Extract error information from URL parameters
  const errorCode = searchParams.get('error_code');
  const errorMessage = searchParams.get('error_message');
  const errorDetails = searchParams.get('error_details');
  const flaggedCategories = searchParams.get('flagged_categories');

  const isContentFlagged = errorCode === '3002' || errorCode === 'CONTENT_FLAGGED';
  const isPaymentFailed = errorCode === 'PAYMENT_FAILED' || errorCode === 'NETWORK_ERROR';

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleRetry = async () => {
    if (!couponId || isCheckingStatus) {
      navigate(`/advertise/coupon/resume?companyId=${companyId}&couponId=${couponId}`, { replace: true });
      return;
    }

    setIsCheckingStatus(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/advertising/${couponId}`, {
        method: 'GET',
        headers: couponApiHeaders(creatorToken, { 'Content-Type': 'application/json' })
      });

      const result = await response.json();

      if (isCouponTokenForbidden(response, result)) {
        wizardCtx.clearAllData();
        alert(COUPON_TOKEN_INVALID_MESSAGE);
        navigate('/advertise/coupon/short-term/create-coupon/1?edit=false', { replace: true });
        setIsCheckingStatus(false);
        return;
      }

      if (response.ok && result.status === 'success' && result.data) {
        const coupon = result.data;
        if (coupon.status === 'paid' || coupon.paymentStatus === 'completed') {
          alert('This coupon has already been paid. Redirecting to success page.');
          navigate('/payment/success', { replace: true });
          setIsCheckingStatus(false);
          return;
        }
      }
    } catch (error) {
      console.error('Error checking coupon status:', error);
    } finally {
      setIsCheckingStatus(false);
    }

    navigate(`/advertise/coupon/resume?companyId=${companyId}&couponId=${couponId}`, { replace: true });
  };

  return (
    <div className="payment-error-container">
      <div className="payment-error-content">
        {/* Error Icon */}
        <div className="payment-error-icon">
          <div className="payment-error-icon-inner">
            <span className="payment-error-icon-text">
              !
            </span>
          </div>
        </div>

        {/* Main Title */}
        <h1 className="payment-error-title">
          Oops
        </h1>

        {/* Error Message */}
        <p className="payment-error-message">
          {errorMessage || 'The payment has not been completed successfully!'}
        </p>

        {/* Error Details (if available) */}
        {errorDetails && (
          <p className="payment-error-details">
            {errorDetails}
          </p>
        )}

        {/* Flagged Categories (if content is flagged) */}
        {isContentFlagged && flaggedCategories && (
          <div className="payment-error-categories">
            <strong>Flagged Categories:</strong> {flaggedCategories.split(',').join(', ')}
          </div>
        )}

        {/* Separator Line */}
        <div className="payment-error-separator" />

        {/* Action Buttons */}
        <div className="payment-error-actions">
          {/* Only show Retry button for payment failures, not content flagged errors */}
          {!isContentFlagged && (
            <button
              onClick={handleRetry}
              disabled={isCheckingStatus}
              className="payment-error-btn-primary"
            >
              {isCheckingStatus ? 'CHECKING...' : 'RETRY'}
            </button>
          )}
          <button
            onClick={handleGoHome}
            className="payment-error-btn-secondary"
          >
            GO TO HOME
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentError;
